/**
 * Little numbers that rise off somebody's head and fade. "+1" for a coin.
 *
 * Pure and time-injected, in the same spirit as Bubbles and SnapshotBuffer: the
 * engine hands it an event and asks what to draw, and nothing here touches a
 * canvas.
 *
 * A LIST, NOT A MAP KEYED BY PLAYER — and that one difference from Bubbles is
 * the entire reason this is a separate module rather than a method on it.
 * Bubbles holds ONE entry per player and REPLACES it, which is right for
 * speech: a tower of stacked sentences is unreadable, and the chat panel keeps
 * everything anyway. Neither is true here. Two coins a second apart must show
 * two numbers rising at different heights, or picking up a handful of coins
 * quietly looks like picking up one. And a "+1" must never stamp out what
 * somebody was saying.
 */

/**
 * How long one number lives.
 *
 * Long enough to read at a glance, short enough that it does not trail behind
 * a player who is still walking — the number is anchored to them, so a long
 * life reads as a label rather than a reward.
 */
export const FLOATER_MS = 900;

/** How far it rises over that life, in world units. About 42 screen pixels. */
export const FLOATER_RISE = 14;

export default class Floaters {
  /**
   * @param {object} [options]
   * @param {() => number} [options.now] - injected so tests do not wait 900ms.
   */
  constructor({ now = () => performance.now() } = {}) {
    this.now = now;
    /** @type {{shortId: number, text: string, startedAt: number}[]} */
    this.items = [];
  }

  /**
   * Start a number rising over somebody.
   *
   * @param {number} shortId - who it belongs to, in this room's id space
   * @param {string} text - already formatted, e.g. "+1"
   */
  add(shortId, text) {
    if (typeof shortId !== 'number' || !text) return;
    this.items.push({ shortId, text, startedAt: this.now() });
  }

  /**
   * Every live number, with its drift and fade already worked out.
   *
   * SWEPT ON READ, like `Bubbles.for` — the engine asks once a frame anyway, so
   * a timer per floater would be pure overhead and one more thing to cancel.
   *
   * NO `prune(visible)`, and the absence is deliberate rather than an
   * oversight. A Bubbles entry is keyed by a player who can walk off screen and
   * keep it alive for five seconds, so that class needs a way to drop entries
   * for people nobody can see. A floater dies in 900ms on its own whether or
   * not anybody is left to draw it. There is nothing here to leak.
   *
   * @returns {{shortId: number, text: string, dy: number, alpha: number}[]}
   */
  active() {
    const now = this.now();
    this.items = this.items.filter((f) => now - f.startedAt < FLOATER_MS);

    return this.items.map((f) => {
      const progress = (now - f.startedAt) / FLOATER_MS;
      return {
        shortId: f.shortId,
        text: f.text,
        dy: -FLOATER_RISE * progress,
        /**
         * Held, then gone — not a straight line down.
         *
         * A linear fade starts dying on the first frame, which reads as a
         * rendering fault rather than a reward. Squaring the progress keeps it
         * near full opacity for the first half of its life and does the
         * disappearing at the end, where it belongs.
         */
        alpha: 1 - progress * progress,
      };
    });
  }

  /** Drop everything. Short ids are per-room, so a map change invalidates all. */
  clear() {
    this.items = [];
  }
}
